import React, { useCallback, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { actionType, modalActionType } from 'utils/constants';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components/macro';

import { FormValues } from 'models/documents';
import { OpenWfDetails } from 'models/response';

import utils from 'utils';
import actions from 'state/actions';
import services from 'services';
import selectors from 'state/selectors';

import CommonButton from 'components/Buttons/CommonButton';
import useCheckBeforeAction from '../hooks';

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface SapStatusIndicatorProps {
  registrationProtocol?: string;
  protocolIn?: string;
  showText?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const ExportToSap = () => {
  const t = utils.intl.useTranslator();
  const idProtocol = useSelector(selectors.documents.selectActiveDocumentID);
  const idUser = useSelector(selectors.user.getIdUser) || -1;
  const idProgram = useSelector(selectors.app.getProgramCode) || -1;
  const record = useSelector(selectors.documents.selectActiveDocument);
  const { values } = useFormikContext<FormValues>();
  const dispatch = useDispatch();
  const isJiesRunning = useSelector(selectors.documents.selectIsJiesRunning);
  const engineInfo = useSelector(selectors.documents.selectEngineInfo);
  const history = useHistory();
  const documentHeader = useSelector(selectors.documents.selectDocumentHeader);
  const saveDocument = utils.hooks.useSaveDocument(true);
  const { checkDocuments } = useCheckBeforeAction(actionType.EXPORT_TO_SAP);
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const rows = useSelector(selectors.documents.selectTableRows);
  const [hasRegProtocol, setHasRegProtocol] = useState<boolean>(false);

  const endExecution = useCallback(() => {
    idProtocol && dispatch(actions.documents.removeRows([idProtocol]));
    dispatch(actions.documents.openNextDocument(history));
  }, [dispatch, history, idProtocol]);

  const openSendToWf = useCallback(
    (data: OpenWfDetails) => {
      dispatch(
        actions.modal.openModal(modalActionType.documents.SEND_TO_WORKFLOW, {
          data: data,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (protocol) {
      const registrationProtocol = rows.find((ele) => ele.protocol === protocol)?.registrationProtocol;
      setHasRegProtocol(registrationProtocol);
    }
  }, [protocol, rows]);

  const exportToSap = useCallback(async () => {
    try {
      if (idProtocol && idProgram && idUser && engineInfo) {
        dispatch(actions.documents.setIsDocReadOnly(true));
        const dataSapIntegration = await services.sapIntegrationTransactionExecute({
          port: engineInfo.clientPort,
          data: {
            flowName: 'ROOT_INTERACTIVE',
            input: {
              idProtocol,
              idProgram,
            },
            parameters: {
              ID_USER: idUser,
              LOG_ENTITY_TYPE: 'id_protocol',
              LOG_ENTITY_ID: idProtocol,
              ID_COMPANY: record?.idCompany,
            },
          },
        });
        dispatch(actions.documents.setIsDocReadOnly(false));
        if (dataSapIntegration) {
          switch (dataSapIntegration.next) {
            case 'sap_user_login':
              dispatch(
                actions.modal.openModal(modalActionType.documents.SAP_USER_LOGIN, {
                  environment: dataSapIntegration.message,
                  port: engineInfo.clientPort,
                  exportToSap,
                }),
              );
              break;
            case 'archive':
            case 'openWf':
              utils.app.notify('success', dataSapIntegration.message ?? '', 10000);
              utils.app.notify('success', t('operation_executed'), 5000);
              if (dataSapIntegration.description) {
                dispatch(
                  actions.modal.openModal(modalActionType.documents.SHOW_SAP_DATA, {
                    message: dataSapIntegration.description,
                    endExecution,
                  }),
                );
              } else {
                endExecution();
              }
              break;
            case 'openWf_manual':
              dispatch(actions.documents.setDocumentFields(idProtocol, idProgram));
              utils.app.notify('success', dataSapIntegration.message ?? '', 10000);
              dispatch(
                actions.modal.openModal(modalActionType.documents.SHOW_SAP_DATA, {
                  message: dataSapIntegration.description,
                  sendToWfData: dataSapIntegration.openWorkflowRequest,
                  openSendToWf,
                }),
              );
              break;
            default:
              dispatch(actions.documents.setDocumentFields(idProtocol, idProgram));
              utils.app.notify('warning', `${dataSapIntegration.description}`, 5000);
          }
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [dispatch, engineInfo, idProgram, idProtocol, idUser, record, endExecution, openSendToWf, t]);

  const checkMessages = useCallback(async () => {
    try {
      const docFree = await checkDocuments();
      if (!docFree) return;
      if ((await saveDocument()) === null) {
        throw new Error('Error saving document');
      }
      if (idProtocol && idProgram && idUser && engineInfo) {
        dispatch(actions.documents.setIsDocReadOnly(true));
        const dataSapMessages = await services.sapIntegrationTransactionExecute({
          port: engineInfo.clientPort,
          data: {
            flowName: 'ROOT_INTERACTIVE_CHECKS',
            flowConfigRootType: 'GCS',
            input: {
              idProtocol,
              idProgram,
            },
            parameters: {
              ID_USER: idUser,
              LOG_ENTITY_TYPE: 'id_protocol',
              LOG_ENTITY_ID: idProtocol,
              ID_COMPANY: record?.idCompany,
            },
          },
        });
        dispatch(actions.documents.setIsDocReadOnly(false));
        if (dataSapMessages.showPopup) {
          dispatch(
            actions.modal.openModal(modalActionType.documents.SAP_MESSAGES, {
              messages: dataSapMessages.messages ?? [],
              blocking: dataSapMessages.blocking !== undefined ? dataSapMessages.blocking : true,
              exportToSap,
            }),
          );
        } else {
          exportToSap();
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [dispatch, engineInfo, idProgram, idProtocol, idUser, record, saveDocument, exportToSap, checkDocuments]);

  const buttonDisabled =
    hasRegProtocol ||
    isJiesRunning !== 'green' ||
    !values.movTypeHeader ||
    !values.subjectHeader ||
    (!!documentHeader &&
      documentHeader.numberOfDetails !== undefined &&
      documentHeader?.numberOfDetails > 1 &&
      !values.supplierCode);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'F6' && !buttonDisabled) {
        event.preventDefault();
        checkMessages();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [checkMessages, buttonDisabled]);

  return values && documentHeader ? (
    <ButtonContainer>
      <CommonButton
        id="export-to-sap-btn"
        action={() => checkMessages()}
        scope={'tertiary'}
        value={t('export_to_sap')}
        customIcon={hasRegProtocol ? 'info-circle' : 'export'}
        selected={buttonDisabled}
        disabled={buttonDisabled}
        tooltip={hasRegProtocol ? t('posted_to_sap') : t('export_to_sap')}
      />
    </ButtonContainer>
  ) : null;
};

export default ExportToSap;
